import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Loader2, RefreshCw, CheckCircle2, AlertCircle, Save, Tag, List } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/useToastHistory'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'

interface MasterInboxLabel {
  label_name: string
  list_type: 'positive' | 'negative' | 'ooo_bounced' | 'ai_messup'
  active?: boolean
}

interface InboxType {
  inbox_type_id: number
  name: string
  icon: string | null
  order: number | null
  workspace_id: number | null
}

interface ClientWithLists {
  id: string
  name: string
  masterinbox_api_key: string | null
  masterinbox_labels: MasterInboxLabel[] | null
  masterinbox_lists: InboxType[] | null
}

const LIST_TYPE_CONFIG = {
  positive: { label: 'Positive', color: 'bg-green-100 text-green-800 border-green-200', headerColor: 'bg-green-50 border-green-200', emoji: '\u2705', listName: 'Positive' },
  negative: { label: 'Negative', color: 'bg-red-100 text-red-800 border-red-200', headerColor: 'bg-red-50 border-red-200', emoji: '\u274C', listName: 'Negative' },
  ooo_bounced: { label: 'OOO/Bounced', color: 'bg-purple-100 text-purple-800 border-purple-200', headerColor: 'bg-purple-50 border-purple-200', emoji: '\uD83D\uDCE8', listName: 'OOO/Bounced' },
  ai_messup: { label: 'AI Messup', color: 'bg-orange-100 text-orange-800 border-orange-200', headerColor: 'bg-orange-50 border-orange-200', emoji: '\u26A0\uFE0F', listName: 'AI Messup' },
} as const

type ListType = keyof typeof LIST_TYPE_CONFIG

export function MasterInboxLabelsTab() {
  const [labels, setLabels] = useState<MasterInboxLabel[]>([])
  const [originalLabels, setOriginalLabels] = useState<MasterInboxLabel[]>([])
  const [clients, setClients] = useState<ClientWithLists[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [refreshingClient, setRefreshingClient] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    // Detect changes
    const changed = JSON.stringify(labels) !== JSON.stringify(originalLabels)
    setHasChanges(changed)
  }, [labels, originalLabels])

  const fetchData = async () => {
    setIsLoading(true)
    try {
      // Fetch all active clients with API keys
      const { data: clientsData, error } = await supabase
        .from('clients')
        .select('id, name, masterinbox_api_key, masterinbox_labels, masterinbox_lists')
        .eq('status', 'Active')
        .not('masterinbox_api_key', 'is', null)
        .order('name')

      if (error) throw error

      const typedClients = (clientsData || []) as unknown as ClientWithLists[]
      setClients(typedClients)

      // Get labels from the first client that has them (they should all be the same)
      const clientWithLabels = typedClients.find(
        c => c.masterinbox_labels && Array.isArray(c.masterinbox_labels) && c.masterinbox_labels.length > 0
      )

      if (clientWithLabels && clientWithLabels.masterinbox_labels) {
        const labelsWithActive = clientWithLabels.masterinbox_labels.map(l => ({
          ...l,
          active: l.active !== false, // default to true if not set
        }))
        setLabels(labelsWithActive)
        setOriginalLabels(JSON.parse(JSON.stringify(labelsWithActive)))
      }
    } catch (err) {
      console.error('Error fetching data:', err)
      toast({
        title: 'Error',
        description: 'Failed to load labels and client data.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleLabelNameChange = (index: number, newName: string) => {
    const updated = [...labels]
    updated[index] = { ...updated[index], label_name: newName }
    setLabels(updated)
  }

  const handleLabelActiveToggle = (index: number) => {
    const updated = [...labels]
    updated[index] = { ...updated[index], active: !updated[index].active }
    setLabels(updated)
  }

  const handleSaveLabels = async () => {
    setIsSaving(true)
    try {
      // Validate — no empty label names
      const emptyLabels = labels.filter(l => !l.label_name.trim())
      if (emptyLabels.length > 0) {
        toast({
          title: 'Validation Error',
          description: 'Label names cannot be empty.',
          variant: 'destructive',
        })
        return
      }

      // Validate — no duplicate label names
      const names = labels.map(l => l.label_name.trim().toLowerCase())
      const duplicates = names.filter((name, i) => names.indexOf(name) !== i)
      if (duplicates.length > 0) {
        toast({
          title: 'Validation Error',
          description: `Duplicate label names found: ${[...new Set(duplicates)].join(', ')}`,
          variant: 'destructive',
        })
        return
      }

      // Update ALL active clients with API keys
      const clientIds = clients.map(c => c.id)
      if (clientIds.length === 0) {
        toast({
          title: 'No Clients',
          description: 'No active clients with API keys found to update.',
          variant: 'destructive',
        })
        return
      }

      const { error } = await supabase
        .from('clients')
        .update({ masterinbox_labels: labels as any })
        .in('id', clientIds)

      if (error) throw error

      setOriginalLabels(JSON.parse(JSON.stringify(labels)))
      toast({
        title: 'Labels Saved',
        description: `Updated labels for ${clientIds.length} client(s).`,
      })
    } catch (err) {
      console.error('Error saving labels:', err)
      toast({
        title: 'Error',
        description: 'Failed to save labels.',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleRefreshLists = async (clientId: string) => {
    setRefreshingClient(clientId)
    try {
      const response = await supabase.functions.invoke('fetch-masterinbox-labels', {
        body: { client_id: clientId },
      })

      if (response.error) throw response.error

      const data = response.data
      if (data?.success) {
        toast({
          title: 'Lists Refreshed',
          description: `Fetched ${data.inbox_types_count} inbox types for ${data.client_name}.`,
        })
        // Refresh data
        await fetchData()
      } else {
        throw new Error(data?.error || 'Failed to refresh lists')
      }
    } catch (err) {
      console.error('Error refreshing lists:', err)
      toast({
        title: 'Error',
        description: `Failed to refresh lists: ${err instanceof Error ? err.message : 'Unknown error'}`,
        variant: 'destructive',
      })
    } finally {
      setRefreshingClient(null)
    }
  }

  const getListMappingStatus = (client: ClientWithLists): { mapped: number; total: number; missing: string[] } => {
    const requiredListNames = ['Positive', 'Negative', 'OOO/Bounced', 'AI Messup']
    if (!client.masterinbox_lists || !Array.isArray(client.masterinbox_lists)) {
      return { mapped: 0, total: 4, missing: requiredListNames }
    }
    const availableListNames = client.masterinbox_lists.map(l => l.name)
    const missing = requiredListNames.filter(name => !availableListNames.includes(name))
    return { mapped: 4 - missing.length, total: 4, missing }
  }

  const groupedLabels = (['positive', 'negative', 'ooo_bounced', 'ai_messup'] as ListType[]).map(listType => ({
    listType,
    config: LIST_TYPE_CONFIG[listType],
    labels: labels
      .map((label, originalIndex) => ({ ...label, originalIndex }))
      .filter(l => l.list_type === listType),
  }))

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Section 1: Global Labels */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Global Reply Labels
              </CardTitle>
              <CardDescription>
                16 labels used to categorize MasterInbox replies. These labels are the same for all clients and map to 4 lists.
              </CardDescription>
            </div>
            <Button
              onClick={handleSaveLabels}
              disabled={!hasChanges || isSaving}
              size="sm"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Labels
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {groupedLabels.map(({ listType, config, labels: groupLabels }) => (
              <div key={listType} className={`border rounded-lg ${config.headerColor}`}>
                <div className="px-4 py-2 border-b flex items-center gap-2">
                  <span>{config.emoji}</span>
                  <span className="font-semibold text-sm">{config.label}</span>
                  <Badge variant="outline" className={`text-xs ${config.color}`}>
                    {groupLabels.length} labels
                  </Badge>
                  <span className="text-xs text-muted-foreground ml-auto">
                    Maps to list: "{config.listName}"
                  </span>
                </div>
                <div className="p-3 space-y-2 bg-white/50">
                  {groupLabels.map((label) => (
                    <div key={label.originalIndex} className="flex items-center gap-3">
                      <Switch
                        checked={label.active !== false}
                        onCheckedChange={() => handleLabelActiveToggle(label.originalIndex)}
                      />
                      <Input
                        value={label.label_name}
                        onChange={(e) => handleLabelNameChange(label.originalIndex, e.target.value)}
                        className={`flex-1 h-8 text-sm ${label.active === false ? 'opacity-50' : ''}`}
                        disabled={label.active === false}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Per-Client Lists / Inbox Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <List className="h-5 w-5" />
            Per-Client MasterInbox Lists
          </CardTitle>
          <CardDescription>
            Inbox types (lists) fetched from each client's MasterInbox workspace. Each list maps to a reply category type.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active clients with MasterInbox API keys found.</p>
          ) : (
            <div className="space-y-3">
              {clients.map((client) => {
                const status = getListMappingStatus(client)
                const hasLists = client.masterinbox_lists && Array.isArray(client.masterinbox_lists) && client.masterinbox_lists.length > 0

                return (
                  <div key={client.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{client.name}</span>
                        {hasLists ? (
                          status.mapped === status.total ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              All {status.total} lists mapped
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200 text-xs">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              {status.mapped}/{status.total} lists mapped
                            </Badge>
                          )
                        ) : (
                          <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-200 text-xs">
                            No lists fetched
                          </Badge>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRefreshLists(client.id)}
                        disabled={refreshingClient === client.id}
                      >
                        {refreshingClient === client.id ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <RefreshCw className="h-3 w-3 mr-1" />
                        )}
                        Refresh Lists
                      </Button>
                    </div>

                    {hasLists ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {(['positive', 'negative', 'ooo_bounced', 'ai_messup'] as ListType[]).map((listType) => {
                          const config = LIST_TYPE_CONFIG[listType]
                          const matchedList = client.masterinbox_lists?.find(l => l.name === config.listName)

                          return (
                            <div
                              key={listType}
                              className={`border rounded p-2 text-xs ${
                                matchedList ? config.headerColor : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="font-medium mb-1">{config.emoji} {config.label}</div>
                              {matchedList ? (
                                <div className="text-muted-foreground">
                                  <div>{matchedList.icon} {matchedList.name}</div>
                                  <div className="text-[10px] opacity-70">ID: {matchedList.inbox_type_id}</div>
                                </div>
                              ) : (
                                <div className="text-red-500 italic">Not found</div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Click "Refresh Lists" to fetch inbox types from MasterInbox.
                      </p>
                    )}

                    {status.missing.length > 0 && hasLists && (
                      <p className="text-xs text-yellow-600 mt-2">
                        Missing lists: {status.missing.join(', ')}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
